import React, { useEffect, useMemo, useState, ChangeEvent, KeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { Search, Plus, MoreVertical } from "lucide-react";
import BottomNav from "../../components/BottomNav.tsx";
import { baseurly } from "../../utils/apis.ts";
import { X_API_KEY } from "../../utils/apis.ts";
import {useAuth} from "../../context/authContext.tsx";
import { baseurl } from '../../utils/apis.ts';
import MainLayout from "../../layouts/MainLayout.tsx";
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";

// Define the interface for a vehicle model as returned by your API.
interface VehicleModel {
  image: any;
  id: number;
  model_name: string;
  year_make: number | string | null;
  vehicle_type: string;
  // Additional fields from the API can be added here if needed.
}

const TARGET_MODEL_YEAR = 2026;
const RECENT_MODELS_STORAGE_KEY = "crm_recent_vehicle_models";
const MAX_RECENT_MODELS = 6;
const YEAR_FOCUS_LIMIT = 5;

const parseModelYear = (value: number | string | null): number | null => {
  const parsedYear = Number(value);
  return Number.isFinite(parsedYear) ? parsedYear : null;
};

const DashboardPage: React.FC = () => {
  const { employeeI, authReady } = useAuth();
  const navigate = useNavigate();
  const [vehicleModels, setVehicleModels] = useState<VehicleModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State to track which vehicle model’s dropdown is open.
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  // State for inline editing a model name.
  const [editingModelId, setEditingModelId] = useState<number | null>(null);
  const [editModelName, setEditModelName] = useState<string>("");
  const [recentlyOpenedIds, setRecentlyOpenedIds] = useState<number[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const storedIds = window.localStorage.getItem(RECENT_MODELS_STORAGE_KEY);
      if (!storedIds) return [];

      const parsedIds = JSON.parse(storedIds);
      if (!Array.isArray(parsedIds)) return [];

      return parsedIds
        .filter((id): id is number => typeof id === "number")
        .slice(0, MAX_RECENT_MODELS);
    } catch {
      return [];
    }
  });

  // Fetch vehicle models from the API when the component mounts.
  useEffect(() => {
    if (!authReady) {
      return;
    }
    if (!employeeI) {
      navigate("/login", { replace: true });
      return;
    }
    fetch(`${baseurly}/vehicle_models`,{
            
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': X_API_KEY,
          },
          credentials: "include",
        })
    
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch vehicle models");
        }
        return res.json();
      })
      .then((data) => {
        // Assuming the API returns an array of vehicle models.
        setVehicleModels(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching vehicle models:", err);
        setError("Unable to load vehicle models");
        setLoading(false);
      });
  }, [authReady, employeeI, navigate]);

  // Handlers for dropdown actions.
  const handleToggleDropdown = (id: number) => {
    setOpenDropdownId((prev) => (prev === id ? null : id));
  };

  const rememberRecentlyOpened = (modelId: number) => {
    setRecentlyOpenedIds((previousIds) => {
      const nextIds = [modelId, ...previousIds.filter((id) => id !== modelId)].slice(
        0,
        MAX_RECENT_MODELS
      );
      try {
        window.localStorage.setItem(RECENT_MODELS_STORAGE_KEY, JSON.stringify(nextIds));
      } catch {
        // Ignore localStorage errors and keep app behavior intact.
      }
      return nextIds;
    });
  };

  const handleView = (vehicle: VehicleModel) => {
    rememberRecentlyOpened(vehicle.id);
    navigate(`/vehicle/${vehicle.id}`);
  };

  const handleStartEdit = (id: number, currentName: string) => {
    setEditingModelId(id);
    setEditModelName(currentName);
    setOpenDropdownId(null); // Close dropdown when starting to edit.
  };

  const handleEditChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEditModelName(e.target.value);
  };

  const handleEditSubmit = async (id: number) => {
    try {
      const payload = { model_name: editModelName };
      const response = await fetch(`${baseurly}/vehicle_model/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to update vehicle model");
      }
      // Update local state with the new model name.
      setVehicleModels((prev) =>
        prev.map((model) => (model.id === id ? { ...model, model_name: editModelName } : model))
      );
      setEditingModelId(null);
      setEditModelName("");
    } catch (error) {
      console.error("Error updating vehicle model:", error);
    }
  };

  const handleEditKeyDown = (e: KeyboardEvent<HTMLInputElement>, id: number) => {
    if (e.key === "Enter") {
      handleEditSubmit(id);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`${baseurly}/vehicle_model/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete vehicle model");
      }
      // Remove the model from local state.
      setVehicleModels((prev) => prev.filter((model) => model.id !== id));
      setOpenDropdownId(null);
    } catch (error) {
      console.error("Error deleting vehicle model:", error);
    }
  };

  const sortedVehicleModels = useMemo(() => {
    return [...vehicleModels].sort((a, b) => {
      const yearA = parseModelYear(a.year_make);
      const yearB = parseModelYear(b.year_make);
      const distanceA = yearA === null ? Number.POSITIVE_INFINITY : Math.abs(yearA - TARGET_MODEL_YEAR);
      const distanceB = yearB === null ? Number.POSITIVE_INFINITY : Math.abs(yearB - TARGET_MODEL_YEAR);

      if (distanceA !== distanceB) return distanceA - distanceB;
      if (yearA !== yearB) return (yearB ?? Number.NEGATIVE_INFINITY) - (yearA ?? Number.NEGATIVE_INFINITY);
      return a.model_name.localeCompare(b.model_name);
    });
  }, [vehicleModels]);

  const vehicleModelMap = useMemo(() => {
    const modelMap = new Map<number, VehicleModel>();
    vehicleModels.forEach((model) => {
      modelMap.set(model.id, model);
    });
    return modelMap;
  }, [vehicleModels]);

  const recentlyOpenedModels = useMemo(
    () =>
      recentlyOpenedIds
        .map((modelId) => vehicleModelMap.get(modelId))
        .filter((model): model is VehicleModel => model !== undefined),
    [recentlyOpenedIds, vehicleModelMap]
  );

  const modelSnapshot = useMemo(() => {
    return vehicleModels.reduce(
      (summary, model) => {
        const year = parseModelYear(model.year_make);
        summary.totalModels += 1;
        if (year === null) {
          summary.missingYear += 1;
          return summary;
        }

        if (year === TARGET_MODEL_YEAR) summary.targetYear += 1;
        if (Math.abs(year - TARGET_MODEL_YEAR) <= 1) summary.nearTarget += 1;
        if (year > TARGET_MODEL_YEAR) summary.futureYear += 1;
        return summary;
      },
      { totalModels: 0, targetYear: 0, nearTarget: 0, futureYear: 0, missingYear: 0 }
    );
  }, [vehicleModels]);

  const yearFocusModels = useMemo(
    () =>
      sortedVehicleModels
        .filter((model) => parseModelYear(model.year_make) !== null)
        .slice(0, YEAR_FOCUS_LIMIT),
    [sortedVehicleModels]
  );

  return (
    <MainLayout>
      <div className="min-h-screen w-full bg-blue-900 pb-20">
      {/* Header */}
     

      <h1 className="text-2xl text-white font-bold text-center">Welcome {employeeI?.fname}</h1>

      {/* Search Bar */}
      <div className="px-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search for an instruction document..."
            className="w-full py-3 px-4 pr-10 rounded-full"
          />
          <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-900" />
        </div>
      </div>

      {/* Work Instructions Section */}
      <div className="bg-white ring-2 ring-white-500 rounded-t-3xl">
        <div className="px-6 mb-6 bg-white rounded-full p-6">
          <div className="bg-white rounded-xl border p-8 flex justify-between items-center">
            <div>
              <h2 className="text-blue-900 text-xl font-bold mb-2">Production Process Portal</h2>
              <p className="text-gray-600 text-sm max-w-xs">
                Find clear, step-by-step instructions to help you complete your tasks efficiently and accurately.
              </p>
            </div>
            <img
              src={digitalOceanImageUrl("products/kayoola_coach/images/orders-coach/carousel.png")}
              alt="Bus illustration"
              className="w-27 h-24"
            />
          </div>
        </div>

        {/* Vehicle Models Section */}
        <div className="px-6 mt-6">
          <div className="flex justify-between items-center">
            <h2 className="text-blue-900 text-xl font-bold">Vehicle Models</h2>
            {/* Add New Model Button */}
            <div className="text-center font-bold mt-6 hover:font-bold text-white transition-transform transform hover:scale-105">
              <button
                className="bg-blue-900 text-white px-6 py-3 rounded-full flex items-center gap-2 mx-auto"
                onClick={() => navigate("/admin/add-models")}
              >
                <Plus className="w-5 h-5 font-bold transition-transform transform hover:scale-110" /> Add New Model
              </button>
            </div>
            <div className="text-center font-bold mt-6 hover:font-bold text-white transition-transform transform hover:scale-105">
              <button
                className="bg-blue-900 text-white px-6 py-3 rounded-full flex items-center gap-2 mx-auto"
                onClick={() => navigate("/add-building")}
              >
                <Plus className="w-5 h-5 font-bold transition-transform transform hover:scale-110" /> Add New Workshop
              </button>
            </div>
            <div className="text-center font-bold mt-6 hover:font-bold text-white transition-transform transform hover:scale-105">
              <button
                className="bg-blue-900 text-white px-6 py-3 rounded-full flex items-center gap-2 mx-auto"
                onClick={() => navigate("/select-building")}
              >
                <Plus className="w-5 h-5 font-bold transition-transform transform hover:scale-110" /> Add New Workstation
              </button>
            </div>
          </div>

          {/* Vehicle Models List */}
          <div className="flex gap-4 mt-4 overflow-x-auto">
            {loading ? (
              <p>Loading...</p>
            ) : error ? (
              <p className="text-red-500">{error}</p>
            ) : (
              sortedVehicleModels.map((vehicle) => (
                <div
                  onClick={() => handleView(vehicle)}
                  key={vehicle.id}
                  className="relative bg-white border rounded-xl p-4 min-w-[200px] cursor-pointer hover:shadow-lg transition"
                >
                  <img
                    src={ 
                      vehicle.image 
                      ?
                      `${baseurl}files/${vehicle.image}`
                      :
                      digitalOceanImageUrl("assets/images/b.png")
                    }
                    
                    alt={vehicle.model_name}
                    className="w-full h-32 object-cover rounded-lg mb-2"
                  />
                  {editingModelId === vehicle.id ? (
                    <input
                      type="text"
                      value={editModelName}
                      onChange={handleEditChange}
                      onKeyDown={(e) => handleEditKeyDown(e, vehicle.id)}
                      onBlur={() => handleEditSubmit(vehicle.id)}
                      className="w-full p-1 border rounded mb-2"
                    />
                  ) : (
                    <h3 className="font-bold text-sm">{vehicle.model_name}</h3>
                  )}
                  <p className="text-sm text-gray-600">{vehicle.vehicle_type} {vehicle.year_make}</p>
                  {/* MoreVertical button */}
                  <button
                    type="button"
                    className="absolute top-2 right-2 text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleDropdown(vehicle.id);
                    }}
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  {/* Dropdown menu */}
                  {openDropdownId === vehicle.id && (
                    <div className="absolute top-8 right-2 bg-white border rounded shadow-md z-10">
                      <ul>
                        <li>
                          <button
                            type="button"
                            className="block px-4 py-2 text-sm text-blue-900 hover:bg-gray-100 w-full text-left"
                            onClick={() => handleView(vehicle)}
                          >
                            View
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            className="block px-4 py-2 text-sm text-blue-900 hover:bg-gray-100 w-full text-left"
                            onClick={() => handleStartEdit(vehicle.id, vehicle.model_name)}
                          >
                            Edit
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            className="block px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                            onClick={() => handleDelete(vehicle.id)}
                          >
                            Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Model Insights Section */}
        <div className="px-6 mt-6 mb-6">
          <h2 className="text-blue-900 text-xl font-bold">Model Insights</h2>

          <div className="grid gap-4 mt-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-blue-100 rounded-xl p-4">
              <p className="text-sm text-blue-900">Total Models</p>
              <p className="text-2xl font-bold text-blue-900">{modelSnapshot.totalModels}</p>
            </div>
            <div className="bg-blue-100 rounded-xl p-4">
              <p className="text-sm text-blue-900">{TARGET_MODEL_YEAR} Models</p>
              <p className="text-2xl font-bold text-blue-900">{modelSnapshot.targetYear}</p>
            </div>
            <div className="bg-blue-100 rounded-xl p-4">
              <p className="text-sm text-blue-900">Within +/-1 Year</p>
              <p className="text-2xl font-bold text-blue-900">{modelSnapshot.nearTarget}</p>
            </div>
            <div className="bg-blue-100 rounded-xl p-4">
              <p className="text-sm text-blue-900">Beyond {TARGET_MODEL_YEAR}</p>
              <p className="text-2xl font-bold text-blue-900">{modelSnapshot.futureYear}</p>
            </div>
          </div>

          <div className="grid gap-4 mt-4 lg:grid-cols-2">
            <div className="bg-blue-100 rounded-xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-blue-900">{TARGET_MODEL_YEAR} Year Focus</h3>
                <span className="text-sm text-blue-900">Top {YEAR_FOCUS_LIMIT}</span>
              </div>
              {yearFocusModels.length === 0 ? (
                <p className="text-sm text-gray-600">No vehicle models available yet.</p>
              ) : (
                <div className="space-y-2">
                  {yearFocusModels.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      type="button"
                      className="w-full bg-white rounded-lg p-3 flex items-center justify-between text-left hover:bg-blue-50 transition"
                      onClick={() => handleView(vehicle)}
                    >
                      <div>
                        <p className="font-semibold text-blue-900">{vehicle.model_name}</p>
                        <p className="text-sm text-gray-600">{vehicle.vehicle_type}</p>
                      </div>
                      <span className="text-sm font-semibold text-blue-900">
                        {parseModelYear(vehicle.year_make) ?? "N/A"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-blue-100 rounded-xl p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-3">Recently Opened Models</h3>
              {recentlyOpenedModels.length === 0 ? (
                <p className="text-sm text-gray-600">Open a model to keep it here for quick access.</p>
              ) : (
                <div className="space-y-2">
                  {recentlyOpenedModels.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      type="button"
                      className="w-full bg-white rounded-lg p-3 flex items-center justify-between text-left hover:bg-blue-50 transition"
                      onClick={() => handleView(vehicle)}
                    >
                      <div>
                        <p className="font-semibold text-blue-900">{vehicle.model_name}</p>
                        <p className="text-sm text-gray-600">{vehicle.vehicle_type}</p>
                      </div>
                      <span className="text-sm font-semibold text-blue-900">
                        {parseModelYear(vehicle.year_make) ?? "N/A"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {modelSnapshot.missingYear > 0 && (
                <p className="text-xs text-gray-600 mt-3">
                  {modelSnapshot.missingYear} model(s) have no year and are excluded from year ranking.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Navigation */}
        <BottomNav showBack={true} />
      </div>
    </div>
    </MainLayout>
    
  );
};

export default DashboardPage;
